'use client';

/**
 * Mutual Transfer Form Page
 * 
 * Global Configuration:
 * - PREFERENCE_EDITING_ENABLED: Set to true/false to enable/disable preference editing for all users
 *   When disabled, users cannot edit their preferences after submission
 */

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ArrowLeft, UserCheck, User, GraduationCap, School, Search, LogIn, AlertCircle, CheckCircle2, Star, MapPin, Loader2, Share2 ,Users} from 'lucide-react';
import BiharHeader from '../../components/BiharHeader';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from '@/components/auth/AuthModal';
import { schoolTypeForClass } from '@/lib/mutual-transfer/subjects';
import PreferenceCards from './PreferenceCards';
import ConfirmationModal from './ConfirmationModal';
import OtpVerificationModal from './OtpVerificationModal';

interface SchoolSearchResult {
  id: number;
  name: string;
  udise: string;
  district_id: number;
  district_name: string;
  block_id: number;
  block_name: string;
  latitude: number;
  longitude: number;
  map_link: string;
  type: string;
  created_at: string;
}

interface ExistingApplication {
  id: number;
  name: string;
  mobile: string;
  gender: string;
  status: string;
  teacher_category: string;
  class: string;
  subject: string;
  current_school_udise: string;
  current_school_name: string;
  school_type: string;
  created_at: string;
  district_name?: string;
  block_name?: string;
  preferences: Array<{
    preference_order: number;
    district: string;
    block: string;
    school: string;
    scope: string;
  }>;
}

// Guide Modal for logged-out users
function GuideModal({ isOpen, onClose, onStartForm }: { isOpen: boolean; onClose: () => void; onStartForm: () => void }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-3">
      <div className="bg-gradient-to-br from-white via-zinc-50 to-slate-100 rounded-2xl shadow-2xl p-4 max-w-2xl w-full relative border border-zinc-200/50 max-h-[88vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 bg-zinc-100 hover:bg-zinc-200 rounded-full flex items-center justify-center text-zinc-600 hover:text-zinc-800 transition-all duration-200 z-10"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header Section */}
        <div className="text-center mb-3">
          {/* Title */}
          <h2 className="text-lg font-bold text-zinc-900 mb-0.5">
            Form Fill करने का Complete Guide
          </h2>

          {/* Subtitle */}
          <p className="text-xs text-zinc-600">
            Step by step instructions to fill mutual transfer form / पारस्परिक स्थानांतरण फॉर्म भरने के लिए चरणबद्ध निर्देश
          </p>
        </div>

        {/* Instructions Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* English Instructions */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              English Instructions
            </h3>
            <div className="space-y-1.5 text-xs text-blue-800">
              <div className="flex items-start space-x-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">1</span>
                <div>
                  <strong>Personal Information:</strong> Fill your name, mobile number, and gender correctly
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">2</span>
                <div>
                  <strong>Teacher Details:</strong> Select your teacher category (BPSC/Exclusive), class, and subject
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">3</span>
                <div>
                  <strong>Current School:</strong> Search and select your current school by name or UDISE code
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">4</span>
                <div>
                  <strong>Transfer Preferences:</strong> Add up to 10 districts/blocks where you want to transfer
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">5</span>
                <div>
                  <strong>Submit:</strong> Review all information and submit your application
                </div>
              </div>
            </div>
          </div>

          {/* Hindi Instructions */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <h3 className="text-sm font-bold text-green-900 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              हिंदी निर्देश
            </h3>
            <div className="space-y-1.5 text-xs text-green-800">
              <div className="flex items-start space-x-2">
                <span className="bg-green-200 text-green-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">1</span>
                <div>
                  <strong>व्यक्तिगत जानकारी:</strong> अपना नाम, मोबाइल नंबर और लिंग सही भरें
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-green-200 text-green-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">2</span>
                <div>
                  <strong>शिक्षक विवरण:</strong> अपनी शिक्षक श्रेणी (BPSC/एक्सक्लूसिव), कक्षा और विषय चुनें
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-green-200 text-green-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">3</span>
                <div>
                  <strong>वर्तमान स्कूल:</strong> नाम या UDISE कोड से अपना वर्तमान स्कूल खोजें और चुनें
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-green-200 text-green-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">4</span>
                <div>
                  <strong>स्थानांतरण प्राथमिकताएं:</strong> जहां आप स्थानांतरण चाहते हैं वहां 5 जिलों/ब्लॉक जोड़ें
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="bg-green-200 text-green-800 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold mt-0.5">5</span>
                <div>
                  <strong>सबमिट करें:</strong> सभी जानकारी की समीक्षा करें और अपना आवेदन जमा करें
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons — YouTube + Start Form side by side (50/50) */}
        <div className="grid grid-cols-2 gap-2">
          {/* YouTube Button */}
          <a
            href="https://www.youtube.com/watch?v=0cD3-djPagU"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center justify-center gap-0.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-2.5 px-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
          >
            <div className="flex items-center gap-1.5">
              <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="text-sm font-bold leading-tight">Watch on YouTube</span>
            </div>
            <span className="text-[10px] opacity-90">Video guide</span>
          </a>

          {/* Start Form Button */}
          <button
            onClick={() => {
              onClose();
              onStartForm();
            }}
            className="flex flex-col items-center justify-center gap-0.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-2.5 px-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
          >
            <div className="flex items-center gap-1.5">
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-bold leading-tight">Start Form</span>
            </div>
            <span className="text-[10px] opacity-90">फॉर्म भरना शुरू करें</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MutualTransferFormPage() {
  const { user, loading, checkAuth } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    gender: '',
    teacherCategory: '',
    class: '',
    subject: '',
    currentSchoolUdise: '',
    currentSchoolName: '',
    blockName: '',
    districtName: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schoolSearchResults, setSchoolSearchResults] = useState<SchoolSearchResult[]>([]);
  const [showSchoolSearch, setShowSchoolSearch] = useState(false);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showOtpVerificationModal, setShowOtpVerificationModal] = useState(false);
  const [existingApplication, setExistingApplication] = useState<ExistingApplication | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // WhatsApp OTP verification states.
  // Re-enabled 2 Aug 2026: the WABA restriction cleared, OTPs deliver again —
  // the Send OTP button is back and submission requires verification (the
  // server-side twin lives in api/bihar/mutual-transfer/route.ts).
  const PHONE_OTP_ENABLED = true;

  // Claim gate (1 Aug 2026): accounts with no application see a two-field ask
  // (mobile + UDISE) before the form. A match adopts the existing application
  // onto this account (WhatsApp-signup users can't log in during the WABA
  // restriction and would otherwise refill and hit the unique-mobile error) —
  // see /api/bihar/mutual-transfer/claim.
  // Disabled 2 Aug 2026: the WABA restriction cleared, so phone-only users can
  // log in with their mobile number again (WhatsApp OTP) and reach their
  // existing application directly — there's no need to link a Google account to
  // a number. Kept off but code intact in case the flow is needed again.
  const CLAIM_GATE_ENABLED = false;
  const [claimGateDone, setClaimGateDone] = useState(false);
  const [claimMobile, setClaimMobile] = useState('');
  const [claimUdise, setClaimUdise] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimLinked, setClaimLinked] = useState(false);
  // The UDISE field appears only after the server says this number already has
  // an application — new teachers just enter their number and move on.
  const [claimNeedUdise, setClaimNeedUdise] = useState(false);

  const handleClaimCheck = async () => {
    const m = claimMobile.replace(/\D/g, '').slice(-10);
    const u = claimUdise.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(m)) {
      setClaimError('सही 10 अंकों का मोबाइल नंबर डालें');
      return;
    }
    if (claimNeedUdise && u.length < 8) {
      setClaimError('सही UDISE कोड डालें');
      return;
    }
    setClaimBusy(true);
    setClaimError('');
    try {
      const res = await fetch('/api/bihar/mutual-transfer/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mobile: m, udise: u }),
      });
      const d = await res.json();
      if (!res.ok) {
        setClaimError(d.error || 'कुछ गलत हो गया, दोबारा कोशिश करें');
        return;
      }
      if (d.linked) {
        // Their old application now belongs to this account — matches await.
        setClaimLinked(true);
        setTimeout(() => {
          window.location.href = '/dashboard?section=matches';
        }, 2500);
        return;
      }
      if (d.needUdise) {
        setClaimNeedUdise(true);
        setClaimError('');
        return;
      }
      if (d.useEmail) {
        setClaimError(
          'यह नंबर पहले से एक Google/ईमेल अकाउंट से जुड़ा है। कृपया उसी ईमेल से लॉगिन करें जिससे आपने पहले रजिस्टर किया था।',
        );
        return;
      }
      if (d.found) {
        setClaimError(
          'यह नंबर पहले से रजिस्टर्ड है लेकिन UDISE कोड मेल नहीं खाया। नंबर और UDISE कोड जाँच कर दोबारा कोशिश करें।',
        );
        return;
      }
      // Nothing registered on this number: fresh form, mobile carried over.
      setFormData((prev) => ({ ...prev, mobile: m }));
      setClaimGateDone(true);
    } catch {
      setClaimError('कुछ गलत हो गया, दोबारा कोशिश करें');
    } finally {
      setClaimBusy(false);
    }
  };
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  // The guide modal used to auto-open 2s after load for logged-out visitors
  // (re-arming every 10 minutes). It grabbed attention ahead of the form and
  // dropped people, so it is now MANUAL-ONLY: a small floating "फॉर्म कैसे
  // भरें?" pill (guideHelpButton below) opens it on demand.
  const guideHelpButton = (
    <button
      onClick={() => setIsGuideModalOpen(true)}
      className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 border border-zinc-300 shadow-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-50 hover:shadow-xl"
    >
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-zinc-800 text-white text-[10px]">?</span>
      फॉर्म कैसे भरें?
    </button>
  );

  // Auto-populate mobile field with verified phone number when user data changes
  useEffect(() => {
    if (user && user.phone && user.is_verified && !formData.mobile) {
      // Extract the 10-digit number from the phone (remove +91 prefix)
      const phoneNumber = user.phone.replace(/^\+91/, '');
      
      // Update the form data with the verified phone number
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs verified phone into form; multiple interdependent triggers (user, formData.mobile, errors.mobile) — render-phase conversion would change re-population behavior
      setFormData(prev => ({
        ...prev,
        mobile: phoneNumber
      }));
      
      // Clear any mobile field errors
      if (errors.mobile) {
        setErrors(prev => ({
          ...prev,
          mobile: ''
        }));
      }
    }
  }, [user, formData.mobile, errors.mobile]);
  
  // Global toggle to enable/disable preference editing
  // Set this to false to disable preference editing for all users
  const PREFERENCE_EDITING_ENABLED = false;

  // Show WhatsApp group modal only after form submission or when an existing application is present
  // Auto-open timers for unfilled forms have been removed as per requirement

  const [schoolType, setSchoolType] = useState<'primary' | 'other'>('primary');

  // Preference state shape:
  // { district: string, block: string, school: string, scope: 'district' | 'block' | 'school' }
  const [preferences, setPreferences] = useState([
    { district: '', block: '', school: '', scope: 'district' as 'district' | 'block' | 'school' }
  ]);

  // Edit preferences state for existing applications
  const [editPreferences, setEditPreferences] = useState<Array<{
    district: string;
    block: string;
    school: string;
    scope: 'district' | 'block' | 'school';
  }>>([]);
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);

  // Subject options mapping by class
  type SubjectOption = { value: string; label: string };
  interface SubjectOptionsByClass {
    [key: string]: SubjectOption[];
  }
  const subjectOptionsByClass: SubjectOptionsByClass = {
    "1-5": [
      { value: "hindi", label: "Hindi (General) / हिंदी (सामान्य)" },
      { value: "english", label: "English / अंग्रेजी" },
      { value: "urdu", label: "Urdu / उर्दू" },
      { value: "bengali", label: "Bengali / बांग्ला" },
      { value: "mathematics", label: "Mathematics / गणित" },
      { value: "environmental-studies", label: "Environmental Studies / पर्यावरण अध्ययन" },
    ],
    "6-8": [
      { value: "hindi", label: "Hindi / हिन्दी" },
      { value: "english", label: "English / अंग्रेजी" },
      { value: "urdu", label: "Urdu / उर्दू" },
      { value: "sanskrit", label: "Sanskrit / संस्कृत" },
      { value: "mathematics", label: "Mathematics / गणित" },
      { value: "science", label: "Science / विज्ञान" },
      { value: "social-science", label: "Social Science (History, Geography, Civics, Economics) / सामाजिक विज्ञान (इतिहास, भूगोल, नागरिक शास्त्र, अर्थशास्त्र)" },
    ],
    "9-10": [
      { value: "hindi", label: "Hindi / हिन्दी" },
      { value: "english", label: "English / अंग्रेजी" },
      { value: "urdu", label: "Urdu / उर्दू" },
      { value: "bengali", label: "Bengali / बांग्ला" },
      { value: "sanskrit", label: "Sanskrit / संस्कृत" },
      { value: "arabic", label: "Arabic / अरबी" },
      { value: "persian", label: "Persian / फारसी" },
      { value: "maithili", label: "Maithili / मैथिली" },
      { value: "mathematics", label: "Mathematics / गणित" },
      { value: "science", label: "Science / विज्ञान" },
      { value: "social-science", label: "Social Science (History, Geography, Civics, Economics) / सामाजिक विज्ञान (इतिहास, भूगोल, नागरिक शास्त्र, अर्थशास्त्र)" },
      { value: "fine-arts", label: "Fine Arts / ललित कला" },
      { value: "dance", label: "Dance / नृत्य" },
      { value: "physical-education", label: "Physical Education / शारीरिक शिक्षा" },
      { value: "music", label: "Music / संगीत" },
    ],
    "11-12": [
      { value: "hindi", label: "Hindi / हिन्दी" },
      { value: "urdu", label: "Urdu / उर्दू" },
      { value: "english", label: "English / अंग्रेजी" },
      { value: "sanskrit", label: "Sanskrit / संस्कृत" },
      { value: "bangla", label: "Bangla / बांग्ला" },
      { value: "persian", label: "Persian / फारसी" },
      { value: "arabic", label: "Arabic / अरबी" },
      { value: "pali", label: "Pali / पालि" },
      { value: "prakrit", label: "Prakrit / प्राकृत" },
      { value: "mathematics", label: "Mathematics / गणित" },
      { value: "physics", label: "Physics / भौतिक विज्ञान" },
      { value: "chemistry", label: "Chemistry / रसायन शास्त्र" },
      { value: "biology", label: "Biology / जीव विज्ञान" },
      { value: "botany", label: "Botany / वनस्पति विज्ञान" },
      { value: "zoology", label: "Zoology / प्राणी विज्ञान" },
      { value: "computer-science", label: "Computer Science / कम्प्यूटर सायंस" },
      { value: "multimedia-web-tech", label: "Multimedia & Web Technology / मल्टीमीडिया एवं वेब टेक्नॉलॉजी" },
      { value: "history", label: "History / इतिहास" },
      { value: "political-science", label: "Political Science / राजनीति शास्त्र" },
      { value: "geography", label: "Geography / भूगोल" },
      { value: "economics", label: "Economics / अर्थशास्त्र" },
      { value: "sociology", label: "Sociology / समाजशास्त्र" },
      { value: "psychology", label: "Psychology / मनोविज्ञान" },
      { value: "philosophy", label: "Philosophy / दर्शनशास्त्र" },
      { value: "home-science", label: "Home Science / गृह विज्ञान" },
      { value: "business-studies", label: "Business Studies / बिजनेस स्टडीज" },
      { value: "accountancy", label: "Accountancy / एकाउन्टेन्सी" },
      { value: "entrepreneurship", label: "Entrepreneurship / इंटरप्रेनरशिप" },
      { value: "music", label: "Music / संगीत" },
    ],
  };

  // Function to check for existing application
  const checkExistingApplication = useCallback(async () => {
    if (!user) {
      setIsCheckingExisting(false);
      return;
    }

    try {
      const response = await fetch('/api/bihar/mutual-transfer', {
        credentials: 'include' // Send cookies with the request
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setExistingApplication(result.application);
        }
      }
    } catch (error) {
      console.error('Error checking existing application:', error);
    } finally {
      setIsCheckingExisting(false);
    }
  }, [user]);

  // Check for existing application on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- invokes async loader (checkExistingApplication) that fetches and also sets flags synchronously in its guard
    checkExistingApplication();
  }, [user, checkExistingApplication]);

  // Check if user's phone is already verified
  useEffect(() => {
    if (user?.phone && user?.is_verified) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs verified-phone state from user; must fire on mount when user is preloaded, and ordering with the phone-sync effect above must be preserved
      setIsPhoneVerified(true);
      // Extract only the last 10 digits from the phone number (remove country code)
      const phoneNumber = user.phone.replace(/^\+?91/, '').slice(-10);
      setFormData(prev => ({ ...prev, mobile: phoneNumber }));
    }
  }, [user]);

  // Resend timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const handleShareProfile = async () => {
    if (!existingApplication) {
      alert('Please submit your application first to share your profile.');
      return;
    }

    try {
      const response = await fetch('/api/bihar/mutual-transfer/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate share link');
      }

      // Open WhatsApp with the generated message
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(result.whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');

    } catch (error) {
      console.error('Error sharing profile:', error);
      alert('Failed to share profile. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'teacherCategory') {
      setFormData(prev => ({
        ...prev,
        teacherCategory: value,
        subject: value === 'headteacher/headmaster' ? 'school-head' : ''
      }));
    } else if (name === "class") {
      setFormData(prev => ({
        ...prev,
        class: value,
        subject: "", // reset subject when class changes
      }));
      // School type follows the class rather than being asked separately —
      // teachers kept picking one that contradicted the other, and it silently
      // decides which schools they can be matched against. See
      // schoolTypeForClass.
      setSchoolType(schoolTypeForClass(value));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Auto-show school search when typing in UDISE field
    if (name === 'currentSchoolUdise') {
      setShowSchoolSearch(true);
      setSchoolSearchQuery(value);
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 10) {
      setFormData(prev => ({
        ...prev,
        mobile: value
      }));
      
      if (errors.mobile) {
        setErrors(prev => ({
          ...prev,
          mobile: ''
        }));
      }
    }
  };

  const scrollToError = (fieldName: string) => {
    let element = document.getElementById(`field-${fieldName}`);
    
    // Special handling for preferences section
    if (fieldName === 'preferences') {
      element = document.getElementById('preferences-section');
    }
    
    if (element) {
      // Add a brief highlight effect
      element.classList.add('ring-2', 'ring-red-500', 'ring-opacity-75');
      
      // Scroll to the element with smooth behavior
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest' 
      });
      
      // Focus the element if it's an input
      if (element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
        setTimeout(() => {
          element.focus();
        }, 500);
      }
      
      // Remove highlight after a brief moment
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-red-500', 'ring-opacity-75');
      }, 2000);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let firstErrorField: string | null = null;

    // Critical: Login check - must be first
    if (!user) {
      newErrors.account = 'You must be logged in to submit the form';
      if (!firstErrorField) firstErrorField = 'account';
    }
    // Note: Account verification will be handled by OTP modal during submission

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      if (!firstErrorField) firstErrorField = 'name';
    }

    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
      if (!firstErrorField) firstErrorField = 'mobile';
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
      if (!firstErrorField) firstErrorField = 'mobile';
    } else if (isPhoneVerified) {
      // If phone is verified, don't show any error
      // The verification status will be shown in the UI instead
    }
    // Note: Phone verification will be handled by OTP modal during submission

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
      if (!firstErrorField) firstErrorField = 'gender';
    }

    if (!formData.teacherCategory) {
      newErrors.teacherCategory = 'Teacher category is required';
      if (!firstErrorField) firstErrorField = 'teacherCategory';
    }

    if (!formData.class) {
      newErrors.class = 'Class is required';
      if (!firstErrorField) firstErrorField = 'class';
    }

    if (formData.teacherCategory !== 'headteacher/headmaster' && !formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
      if (!firstErrorField) firstErrorField = 'subject';
    }

    if (!formData.currentSchoolUdise.trim()) {
      newErrors.currentSchoolUdise = 'Current school UDISE is required';
      if (!firstErrorField) firstErrorField = 'currentSchoolUdise';
    }

    if (!formData.currentSchoolName.trim()) {
      newErrors.currentSchoolName = 'Please select your current school from the search results';
      if (!firstErrorField) firstErrorField = 'currentSchoolUdise'; // Use same field ID as UDISE input
    }

    // Validate preferences - check each preference individually
    if (preferences.length === 0) {
      newErrors.preferences = 'Please add at least one transfer preference';
      if (!firstErrorField) firstErrorField = 'preferences';
    } else {
      // Check each preference for required fields
      for (let i = 0; i < preferences.length; i++) {
        const pref = preferences[i];
        
        if (!pref.district.trim()) {
          newErrors.preferences = `District is required for preference ${i + 1}`;
          if (!firstErrorField) firstErrorField = 'preferences';
          break;
        }
        
        // Validate scope-specific requirements
        if (pref.scope === 'block' && !pref.block.trim()) {
          newErrors.preferences = `Block is required for preference ${i + 1} when scope is block`;
          if (!firstErrorField) firstErrorField = 'preferences';
          break;
        }
        
        if (pref.scope === 'school' && (!pref.block.trim() || !pref.school.trim())) {
          newErrors.preferences = `Block and school are required for preference ${i + 1} when scope is school`;
          if (!firstErrorField) firstErrorField = 'preferences';
          break;
        }
      }
    }

    setErrors(newErrors);
    
    return {
      isValid: Object.keys(newErrors).length === 0,
      firstErrorField
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (!validation.isValid) {
      // Scroll to the first error field
      if (validation.firstErrorField) {
        scrollToError(validation.firstErrorField);
      }
      return;
    }

    // Check if account is already verified
    if (!PHONE_OTP_ENABLED || user?.is_verified) {
      // If already verified (or OTP is off), go directly to confirmation
      setShowConfirmationModal(true);
    } else {
      // If not verified, show OTP verification modal first
      setShowOtpVerificationModal(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        ...formData,
        preferences,
        schoolType
      };

      const response = await fetch('/api/bihar/mutual-transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
        credentials: 'include' // Send cookies with the request
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit application');
      }

      // Success
      alert(`Application submitted successfully! Your Application ID is: ${result.applicationId}\n\nमैच मिलते ही आपको सूचना भेज दी जाएगी।`);
      setShowConfirmationModal(false);

      
      // Re-fetch existing application to update the page state
      await checkExistingApplication();
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Show user-friendly error message
      if (error instanceof Error) {
        alert(`Error: ${error.message}`);
      } else {
        alert('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Debounced search function
  const searchSchools = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSchoolSearchResults([]);
      setIsSearching(false);
      setSearchError('');
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const response = await fetch(`/api/bihar/search?q=${encodeURIComponent(query.trim())}&context=form`);
      
      if (!response.ok) {
        throw new Error('Failed to search schools');
      }

      const data: SchoolSearchResult[] = await response.json();
      setSchoolSearchResults(data);
      
      if (data.length === 0) {
        setSearchError('No schools found. Try a different search term.');
      }
    } catch (error) {
      console.error('School search error:', error);
      setSearchError('Failed to search schools. Please try again.');
      setSchoolSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce search with useEffect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (schoolSearchQuery.trim()) {
        searchSchools(schoolSearchQuery);
      } else {
        setSchoolSearchResults([]);
        setIsSearching(false);
        setSearchError('');
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [schoolSearchQuery, searchSchools]);

  const selectSchool = (school: SchoolSearchResult) => {
    setFormData(prev => ({
      ...prev,
      currentSchoolUdise: school.udise,
      currentSchoolName: school.name,
      blockName: school.block_name,
      districtName: school.district_name
    }));
    setShowSchoolSearch(false);
    setSchoolSearchResults([]);
    setSchoolSearchQuery('');
    setSearchError('');
    
    // Clear any existing error for this field
    if (errors.currentSchoolUdise) {
      setErrors(prev => ({
        ...prev,
        currentSchoolUdise: ''
      }));
    }
    if (errors.currentSchoolName) {
      setErrors(prev => ({
        ...prev,
        currentSchoolName: ''
      }));
    }
  };

  const clearSchoolSelection = () => {
    setFormData(prev => ({
      ...prev,
      currentSchoolUdise: '',
      currentSchoolName: '',
      blockName: '',
      districtName: ''
    }));
    setSchoolSearchQuery('');
    setSearchError('');
  };

  const handleUpdatePreferences = async () => {
    try {
      setIsUpdatingPreferences(true);
      
      const response = await fetch('/api/bihar/mutual-transfer', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          preferences: editPreferences
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update preferences');
      }

      // Refresh the existing application data
      await checkExistingApplication();
      setIsEditMode(false);
      
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert(error instanceof Error ? error.message : 'Failed to update preferences');
    } finally {
      setIsUpdatingPreferences(false);
    }
  };

  const handleEditModeToggle = () => {
    if (!isEditMode) {
      // Initialize edit preferences with current preferences
      setEditPreferences(existingApplication?.preferences.map(pref => ({
        district: pref.district,
        block: pref.block || '',
        school: pref.school || '',
        scope: pref.scope as 'district' | 'block' | 'school'
      })) || []);
    }
    setIsEditMode(!isEditMode);
  };

  const handleLoginSuccess = () => {
    setIsAuthModalOpen(false);
    // The page will automatically re-render when user state changes
  };

  // WhatsApp OTP functions
  const handleSendOtp = async () => {
    if (!formData.mobile || formData.mobile.length !== 10) {
      setOtpError('Please enter a valid 10-digit mobile number');
      return;
    }

    // Additional validation to prevent invalid numbers
    const phoneValidation = validatePhoneNumber(formData.mobile);
    if (!phoneValidation.valid) {
      setOtpError(phoneValidation.error || 'Invalid phone number');
      return;
    }

    setIsSendingOtp(true);
    setOtpError('');

    try {
      const response = await fetch('/api/auth/whatsapp/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: `+91${formData.mobile}`,
          name: user?.name || formData.name || 'User',
          email: user?.email
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      setOtpSent(true);
      setResendTimer(60); // 60 seconds countdown
      setOtpError('');
    } catch (error) {
      console.error('Send OTP error:', error);
      setOtpError(error instanceof Error ? error.message : 'Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      // Use different endpoint for logged-in users vs new users
      const endpoint = user ? '/api/auth/verify-phone' : '/api/auth/whatsapp/verify-otp';
      const requestBody = user 
        ? {
            phoneNumber: `+91${formData.mobile}`,
            otp: otp
          }
        : {
            phoneNumber: `+91${formData.mobile}`,
            otp: otp,
            name: formData.name || 'User',
            email: undefined
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify OTP');
      }

      // Update user verification status
      setIsPhoneVerified(true);
      setOtpSent(false);
      setOtp('');
      setOtpError('');
      
      // Update user context if needed
      if (data.user || data.success) {
        // Update the form with the verified phone number
        if (data.user && data.user.phone && data.user.is_verified) {
          // Extract the 10-digit number from the phone (remove +91 prefix)
          const phoneNumber = data.user.phone.replace(/^\+91/, '');
          
          // Update the form data with the verified phone number
          setFormData(prev => ({
            ...prev,
            mobile: phoneNumber
          }));
          
          // Clear any mobile field errors
          if (errors.mobile) {
            setErrors(prev => ({
              ...prev,
              mobile: ''
            }));
          }
          
          // Update the mutual transfer application's mobile field in the database
          try {
            const updateResponse = await fetch('/api/bihar/mutual-transfer/update-mobile', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ mobile: phoneNumber }),
              credentials: 'include'
            });
            
            if (!updateResponse.ok) {
              console.error('Failed to update application mobile field');
            } else {
              console.log('Application mobile field updated successfully');
            }
          } catch (error) {
            console.error('Error updating application mobile field:', error);
          }
        }
        
        // The user context should automatically update through the auth system
        // Don't reload the page to preserve form data
        // window.location.reload(); // Refresh to get updated user data
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setOtpError(error instanceof Error ? error.message : 'Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  // Phone number validation function
  const validatePhoneNumber = (phone: string) => {
    // Check if it's a valid Indian mobile number format
    if (!/^[6-9]\d{9}$/.test(phone)) {
      return { valid: false, error: 'Please enter a valid 10-digit Indian mobile number' };
    }
    
    // Check for common invalid patterns
    if (phone.startsWith('000') || phone.startsWith('999') || phone.startsWith('888')) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    // Check for sequential numbers
    if (/^(\d)\1{8}$/.test(phone)) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    return { valid: true };
  };

  // OTP Verification Modal handlers
  const handleOtpVerificationSuccess = async () => {
    setShowOtpVerificationModal(false);
    setIsPhoneVerified(true);
    
    // Refresh user data to get updated verification status
    try {
      await checkAuth();
      
      // Get the updated user data to extract the verified phone number
      const response = await fetch('/api/user/profile', {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // If user has a verified phone number, update the form
        if (userData.phone && userData.is_verified) {
          // Extract the 10-digit number from the phone (remove +91 prefix)
          const phoneNumber = userData.phone.replace(/^\+91/, '');
          
          // Update the form data with the verified phone number
          setFormData(prev => ({
            ...prev,
            mobile: phoneNumber
          }));
          
          // Clear any mobile field errors
          if (errors.mobile) {
            setErrors(prev => ({
              ...prev,
              mobile: ''
            }));
          }
          
          // Update the mutual transfer application's mobile field in the database
          try {
            const updateResponse = await fetch('/api/bihar/mutual-transfer/update-mobile', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ mobile: phoneNumber }),
              credentials: 'include'
            });
            
            if (!updateResponse.ok) {
              console.error('Failed to update application mobile field');
            } else {
              console.log('Application mobile field updated successfully');
            }
          } catch (error) {
            console.error('Error updating application mobile field:', error);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    
    // After successful verification, show the confirmation modal
    setShowConfirmationModal(true);
  };

  const handlePhoneNumberChangeFromModal = (newPhone: string) => {
    setFormData(prev => ({ ...prev, mobile: newPhone }));
  };

  // Show existing application if found
  if (existingApplication) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-zinc-100 via-slate-200 to-gray-300 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -inset-10 opacity-30">
              <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-zinc-400 to-slate-500 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-slate-500 to-gray-600 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>
          </div>
          
          <div className="container mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 relative z-10">
            <div className="max-w-6xl mx-auto space-y-3">

              {/* Compact status heading */}
              <div className="flex items-center justify-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <h1 className="text-base sm:text-lg font-bold text-zinc-900">Application Submitted / आवेदन जमा हो गया</h1>
              </div>

              {/* Application Details */}
              <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200">
                <h3 className="text-sm sm:text-base font-bold text-blue-900 mb-2">Application Details / आवेदन विवरण</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-xs sm:text-sm">
                  <div><span className="font-medium text-blue-800">ID: </span><span className="text-blue-700">{existingApplication.id}</span></div>
                  <div><span className="font-medium text-blue-800">Status: </span><span className="text-blue-700 capitalize">{existingApplication.status}</span></div>
                  <div><span className="font-medium text-blue-800">Name: </span><span className="text-blue-700 break-words">{existingApplication.name}</span></div>
                  <div><span className="font-medium text-blue-800">WhatsApp: </span><span className="text-blue-700">{existingApplication.mobile}</span></div>
                  <div><span className="font-medium text-blue-800">Category: </span><span className="text-blue-700">{existingApplication.teacher_category === 'bpsc' ? 'BPSC' : existingApplication.teacher_category === 'exclusive' ? 'Exclusive' : existingApplication.teacher_category === 'headteacher/headmaster' ? 'Head Teacher/Headmaster' : existingApplication.teacher_category}</span></div>
                  <div><span className="font-medium text-blue-800">School Type: </span><span className="text-blue-700">{existingApplication.school_type === 'primary' ? 'Primary' : 'Other'}</span></div>
                  <div><span className="font-medium text-blue-800">Submitted: </span><span className="text-blue-700">{new Date(existingApplication.created_at).toLocaleDateString('en-IN')}</span></div>
                </div>

                <div className="mt-2 pt-2 border-t border-blue-200">
                  <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-1">Current School / वर्तमान स्कूल</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-xs sm:text-sm">
                    <div><span className="font-medium text-blue-800">School: </span><span className="text-blue-700 break-words">{existingApplication.current_school_name || 'Not specified'}</span></div>
                    <div><span className="font-medium text-blue-800">UDISE: </span><span className="text-blue-700 font-mono">{existingApplication.current_school_udise}</span></div>
                    <div><span className="font-medium text-blue-800">District: </span><span className="text-blue-700">{existingApplication.district_name || 'Not available'}</span></div>
                    <div><span className="font-medium text-blue-800">Block: </span><span className="text-blue-700">{existingApplication.block_name || 'Not available'}</span></div>
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t border-blue-200 flex flex-col sm:flex-row items-center justify-between gap-2">
                  <p className="text-xs text-blue-600 text-center sm:text-left">Share your profile to find a match faster / मैच जल्दी पाने के लिए प्रोफाइल शेयर करें</p>
                  <button
                    onClick={handleShareProfile}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    Share on WhatsApp
                  </button>
                </div>
              </div>

              {/* Your Preferences */}
              <div className="bg-emerald-50 rounded-xl p-3 sm:p-4 border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm sm:text-base font-bold text-emerald-900">Your Preferences / आपकी प्राथमिकताएं</h3>
                  {PREFERENCE_EDITING_ENABLED && (
                    <button
                      onClick={handleEditModeToggle}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors text-xs"
                    >
                      {isEditMode ? 'Cancel Edit' : 'Edit Preferences'}
                    </button>
                  )}
                </div>

                {!isEditMode ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {existingApplication.preferences.map((pref: { district: string; block: string; school: string; scope: string; }, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 bg-white rounded-lg px-2 py-1.5 border border-emerald-200">
                        <div className="w-5 h-5 shrink-0 mt-0.5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                          {idx + 1}
                        </div>
                        <p className="text-emerald-700 text-xs break-words">
                          {pref.scope === 'district'
                            ? `${pref.district} (Any block/school)`
                            : pref.scope === 'block'
                            ? `${pref.district} → ${pref.block} (Any school)`
                            : `${pref.district} → ${pref.block} → ${pref.school}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <PreferenceCards
                      preferences={editPreferences}
                      setPreferences={setEditPreferences}
                      schoolType={existingApplication.school_type as 'primary' | 'other'}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleEditModeToggle}
                        className="px-4 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdatePreferences}
                        disabled={isUpdatingPreferences}
                        className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {isUpdatingPreferences ? 'Updating...' : 'Update Preferences'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* What's Next — notifications arrive via browser push while the
                  WABA is restricted, so don't promise WhatsApp messages. */}
              <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200 text-xs text-yellow-800 space-y-1">
                <p className="font-semibold">🚀 Share your profile in WhatsApp groups for a faster match. / प्रोफाइल शेयर करें, जल्दी मैच पाएं।</p>
                <p>• 🔔 नोटिफिकेशन ऑन रखें — मैच मिलते ही आपको यहीं सूचना मिलेगी। / Keep notifications ON to hear the moment a match is found.</p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                <Link
                  href="/dashboard?section=matches"
                  className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                >
                  <Users className="h-5 w-5 mr-2" />
                  View My Matches
                </Link>
                <Link
                  href="/bihar/mutual-transfer"
                  className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-zinc-800 via-zinc-900 to-slate-900 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back to Portal
                </Link>
              </div>
            </div>
          </div>
        </div>
        {guideHelpButton}
        <GuideModal isOpen={isGuideModalOpen} onClose={() => setIsGuideModalOpen(false)} onStartForm={() => setIsAuthModalOpen(true)} />
      </>
    );
  }

  // Show login required message if not authenticated
  if (loading || isCheckingExisting) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-zinc-100 via-slate-200 to-gray-300 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -inset-10 opacity-30">
              <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-zinc-400 to-slate-500 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-slate-500 to-gray-600 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>
          </div>
          
          <BiharHeader
            title="Mutual Transfer Form"
            subtitle="पारस्परिक स्थानांतरण फॉर्म"
            description="Loading your secure form..."
            hindiDescription="आपका सुरक्षित फॉर्म लोड हो रहा है..."
            badgeText="Bihar Education Portal"
            badgeIcon={<UserCheck className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-600 mr-2" />}
          />
          
          <div className="container mx-auto px-4 py-8 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-zinc-300/70 p-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-zinc-100/70 to-slate-100/70 rounded-3xl"></div>
                <div className="relative text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-zinc-200 to-slate-300 rounded-full shadow-lg mb-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-800"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-zinc-900 mb-2">Loading...</h3>
                  <p className="text-zinc-700 animate-pulse">Please wait while we prepare your form</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {guideHelpButton}
        <GuideModal isOpen={isGuideModalOpen} onClose={() => setIsGuideModalOpen(false)} onStartForm={() => setIsAuthModalOpen(true)} />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-zinc-100 via-slate-200 to-gray-300 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -inset-10 opacity-30">
              <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-zinc-400 to-slate-500 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-slate-500 to-gray-600 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>
          </div>

          <BiharHeader
            title="Mutual Transfer Form"
            subtitle="पारस्परिक स्थानांतरण फॉर्म"
            hideBadge
          />

                  <div className="container mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-6 lg:py-10 relative z-10">
          <div className="max-w-5xl mx-auto">
             

              {/* Main Login Card */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl border border-zinc-300/70 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-zinc-100/60 to-slate-100/60"></div>
                
                {/* Header Pattern */}
                
                <div className="relative p-4 sm:p-6 lg:p-10">
                  <div className="text-center mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-zinc-900 mb-1.5 sm:mb-2">
                      Form Fill Instructions / फॉर्म भरने के निर्देश
                    </h2>
                    <p className="text-xs sm:text-sm text-zinc-500 max-w-2xl mx-auto">
                      Login to fill the form and follow the steps below / फॉर्म भरने के लिए लॉगिन करें
                    </p>
                  </div>

                  {/* Form Fill Instructions Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-200 to-emerald-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 sm:h-5 sm:w-5 text-green-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-zinc-900 mb-1 text-sm sm:text-base">Personal Information / व्यक्तिगत जानकारी</h4>
                        <p className="text-xs sm:text-sm text-zinc-700">Fill your name, mobile number, and gender correctly</p>
                        <p className="text-xs text-zinc-600">अपना नाम, मोबाइल नंबर और लिंग सही भरें</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-200 to-cyan-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-blue-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-zinc-900 mb-1 text-sm sm:text-base">Teacher Details / शिक्षक विवरण</h4>
                        <p className="text-xs sm:text-sm text-zinc-700">Select your category, class, and subject</p>
                        <p className="text-xs text-zinc-600">अपनी श्रेणी, कक्षा और विषय चुनें</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-200 to-pink-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <School className="h-4 w-4 sm:h-5 sm:w-5 text-purple-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-zinc-900 mb-1 text-sm sm:text-base">Current School / वर्तमान स्कूल</h4>
                        <p className="text-xs sm:text-sm text-zinc-700">Search and select your current school</p>
                        <p className="text-xs text-zinc-600">अपना वर्तमान स्कूल खोजें और चुनें</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-200 to-red-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-orange-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-zinc-900 mb-1 text-sm sm:text-base">Transfer Preferences / स्थानांतरण प्राथमिकताएं</h4>
                        <p className="text-xs sm:text-sm text-zinc-700">Add up to 10 districts where you want to transfer</p>
                        <p className="text-xs text-zinc-600">जहां आप स्थानांतरण चाहते हैं वहां 10 जिले जोड़ें</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center max-w-md mx-auto">
                    <button 
                      onClick={() => setIsAuthModalOpen(true)}
                      className="w-full sm:w-auto group relative inline-flex items-center justify-center px-6 sm:px-8 py-2.5 sm:py-3.5 bg-gradient-to-r from-zinc-700 via-zinc-800 to-slate-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform text-sm sm:text-base"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-zinc-600 to-slate-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <LogIn className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 relative z-10" />
                      <span className="relative z-10">Start Filling Mutual Form / म्यूचुअल फॉर्म भरना शुरू करें</span>
                    </button>
                  </div>

                </div>
              </div>
            </div>
             {/* Features Section */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 pt-8 sm:pt-12">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg border border-zinc-300/70 hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-200 to-emerald-300 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                  </div>
                  <h3 className="font-semibold text-zinc-900 mb-2 text-sm sm:text-base">Step by Step Guide / चरणबद्ध गाइड</h3>
                  <p className="text-xs sm:text-sm text-zinc-700">Easy to follow instructions</p>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg border border-zinc-300/70 hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-200 to-cyan-300 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <School className="h-5 w-5 sm:h-6 sm:w-6 text-blue-700" />
                  </div>
                  <h3 className="font-semibold text-zinc-900 mb-2 text-sm sm:text-base">School Search / स्कूल खोज</h3>
                  <p className="text-xs sm:text-sm text-zinc-700">Find schools by name or UDISE</p>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg border border-zinc-300/70 hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-200 to-pink-300 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Star className="h-5 w-5 sm:h-6 sm:w-6 text-purple-700" />
                  </div>
                  <h3 className="font-semibold text-zinc-900 mb-2 text-sm sm:text-base">Smart Matching / स्मार्ट मैचिंग</h3>
                  <p className="text-xs sm:text-sm text-zinc-700">AI-powered transfer matching</p>
                </div>
              </div>
          </div>
        </div>

        {/* Auth Modal */}
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)}
          mode="login"
          onLoginSuccess={handleLoginSuccess}
        />
        {guideHelpButton}
        <GuideModal isOpen={isGuideModalOpen} onClose={() => setIsGuideModalOpen(false)} onStartForm={() => setIsAuthModalOpen(true)} />
      </>
    );
  }

  // Claim gate: logged in, no application on this account yet — ask the two
  // identifying facts before showing the form (see handleClaimCheck).
  if (CLAIM_GATE_ENABLED && !existingApplication && !claimGateDone) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-100 via-slate-200 to-gray-300 relative overflow-hidden">
        <BiharHeader title="Mutual Transfer Form" subtitle="पारस्परिक स्थानांतरण फॉर्म" hideBadge />
        <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-10 relative z-10">
          <div className="max-w-md mx-auto bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl border border-zinc-300/70 p-5 sm:p-8">
            {claimLinked ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-zinc-900 mb-2">आपका फॉर्म पहले से भरा हुआ है ✅</h2>
                <p className="text-sm text-zinc-600">
                  आपकी पुरानी जानकारी इस अकाउंट से जोड़ दी गई है। आपको आपके मैच दिखाए जा रहे हैं…
                </p>
                <Loader2 className="w-5 h-5 animate-spin text-zinc-400 mx-auto mt-4" />
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-2">पहले यह बताएं</h2>
                  <p className="text-sm text-zinc-600">
                    अगर आपने पहले फॉर्म भर रखा है तो हम उसे इसी अकाउंट से जोड़ देंगे — दोबारा भरने की
                    ज़रूरत नहीं पड़ेगी।
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-800 mb-1.5">
                      WhatsApp मोबाइल नंबर
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={claimMobile}
                      onChange={(e) => setClaimMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10 अंकों का नंबर"
                      className="w-full px-4 py-3 border border-zinc-400 rounded-xl text-base text-zinc-900 placeholder-zinc-500 bg-white focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600"
                      maxLength={10}
                    />
                  </div>
                  {claimNeedUdise && (
                    <div>
                      <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
                        ✅ यह नंबर पहले से रजिस्टर्ड है! पुष्टि के लिए अपने वर्तमान स्कूल का UDISE कोड
                        डालें — फिर आपका भरा हुआ फॉर्म इसी अकाउंट से जुड़ जाएगा।
                      </div>
                      <label className="block text-sm font-semibold text-zinc-800 mb-1.5">
                        वर्तमान स्कूल का UDISE कोड
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={claimUdise}
                        onChange={(e) => setClaimUdise(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="जैसे 10141200101"
                        className="w-full px-4 py-3 border border-zinc-400 rounded-xl text-base text-zinc-900 placeholder-zinc-500 bg-white focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600"
                        maxLength={11}
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        UDISE कोड आपके स्कूल के रिकॉर्ड/बोर्ड पर लिखा होता है।
                      </p>
                    </div>
                  )}
                  {claimError && (
                    <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{claimError}</span>
                    </div>
                  )}
                  <button
                    onClick={handleClaimCheck}
                    disabled={claimBusy}
                    className="w-full py-4 sm:py-3 rounded-xl bg-gradient-to-r from-zinc-700 to-zinc-900 hover:from-zinc-800 hover:to-black text-white font-bold text-lg sm:text-base disabled:opacity-60"
                  >
                    {claimBusy ? 'जाँच हो रही है…' : 'आगे बढ़ें'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-zinc-100 via-slate-200 to-gray-300 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -inset-10 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-zinc-400 to-slate-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-slate-500 to-gray-600 rounded-full blur-3xl animate-pulse delay-700"></div>
          </div>
        </div>

        {/* Compact title */}
        <div className="text-center pt-3 pb-1 px-4 relative z-10">
          <h1 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-zinc-700 via-zinc-800 to-zinc-900 bg-clip-text text-transparent leading-tight">
            Mutual Transfer Form
          </h1>
          <p className="text-base sm:text-lg font-semibold text-zinc-600 mt-0.5">पारस्परिक स्थानांतरण फॉर्म</p>
        </div>

        <div className="container mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-3 relative z-10">
          <div className="max-w-3xl lg:max-w-6xl mx-auto">

            {/* User Welcome Card — compact */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm border border-zinc-300/70 px-3 py-2.5 mb-3 flex items-center gap-3">
              <div className="shrink-0 w-9 h-9 bg-gradient-to-r from-zinc-200 to-slate-300 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-zinc-700" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm sm:text-base font-bold text-zinc-900 leading-tight truncate">Welcome back, {user.name}!</h2>
                <p className="text-xs text-zinc-500 truncate">{user.email}</p>
              </div>
            </div>

            {/* Main Form Container */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl border border-zinc-300/70 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-100/40 to-slate-100/40"></div>
              
              <div className="relative p-3 sm:p-5 lg:p-6">
                                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    
                    {/* Account Verification Status — the unverified warning only
                        makes sense while OTP verification is possible; with
                        PHONE_OTP_ENABLED off it would demand the impossible. */}
                    {user && (user.is_verified || PHONE_OTP_ENABLED) && (
                      <div className={`p-2.5 border rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2 ${
                        user.is_verified 
                          ? 'bg-green-50 border-green-200 text-green-800' 
                          : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                      }`}>
                        {user.is_verified ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            <div>
                              <div className="font-semibold">Account Verified ✅</div>
                              <div className="text-xs opacity-90">Your account is verified and ready for form submission</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
                            <div>
                              <div className="font-semibold">Account Verification Required ⚠️</div>
                              <div className="text-xs opacity-90">You must verify your WhatsApp number with OTP before submitting the form</div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                  

                  {/* Personal Information */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-200 to-cyan-300 rounded-full flex items-center justify-center shadow-lg">
                        <User className="h-5 w-5 sm:h-6 sm:w-6 text-blue-700" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl lg:text-lg font-bold text-zinc-900">Personal Information</h3>
                        <p className="text-zinc-700 text-xs sm:text-sm">व्यक्तिगत जानकारी</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Full Name / पूरा नाम *
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="field-name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border border-zinc-400 rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 placeholder-zinc-700 text-sm sm:text-base"
                          placeholder="Enter your full name"
                        />
                        {errors.name && <p className="text-sm text-red-600 font-medium">{errors.name}</p>}
                      </div>

                      {/* WhatsApp Number with OTP Verification */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          WhatsApp Number / व्हाट्सएप नंबर *
                        </label>
                        <div className="space-y-3">
                          <div className="flex gap-2 items-stretch">
                            <div className="flex-1">
                              <input
                                type="tel"
                                name="mobile"
                                id="field-mobile"
                                value={formData.mobile}
                                onChange={handleMobileChange}
                                disabled={isPhoneVerified}
                                readOnly={isPhoneVerified}
                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border rounded-xl transition-all duration-200 shadow-sm text-sm sm:text-base ${
                                  isPhoneVerified
                                    ? 'border-zinc-200 bg-zinc-100 text-zinc-500 cursor-not-allowed select-none'
                                    : 'border-zinc-400 bg-white/90 backdrop-blur-sm hover:shadow-md text-zinc-900 placeholder-zinc-700 focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600'
                                }`}
                                placeholder="Enter 10-digit WhatsApp number"
                                maxLength={10}
                              />
                            </div>
                            {PHONE_OTP_ENABLED && !isPhoneVerified && formData.mobile.length === 10 && (
                              <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={isSendingOtp || otpSent}
                                className="shrink-0 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                              >
                                {isSendingOtp ? (
                                  <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Sending...
                                  </div>
                                ) : otpSent ? (
                                  'OTP Sent'
                                ) : (
                                  'Send OTP'
                                )}
                              </button>
                            )}
                            {isPhoneVerified && (
                              <div className="shrink-0 flex items-center px-4 py-2.5 bg-green-600 text-white rounded-xl">
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                <span className="text-sm font-medium">Verified</span>
                              </div>
                            )}
                          </div>

                          {/* OTP Input Section */}
                          {otpSent && !isPhoneVerified && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-blue-800 text-sm">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                  <span>OTP sent to +91{formData.mobile}. Check your WhatsApp messages.</span>
                                </div>
                                
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Enter 6-digit OTP"
                                    value={otp}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                      setOtp(value);
                                      setOtpError('');
                                    }}
                                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono text-lg text-black font-bold"
                                    maxLength={6}
                                  />
                                  <button
                                    type="button"
                                    onClick={handleVerifyOtp}
                                    disabled={isVerifyingOtp || otp.length !== 6}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                  >
                                    {isVerifyingOtp ? (
                                      <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Verifying...
                                      </div>
                                    ) : (
                                      'Verify'
                                    )}
                                  </button>
                                </div>

                                {/* Resend OTP */}
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-blue-700">
                                    Didn't receive OTP?
                                  </span>
                                  <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendTimer > 0 || isSendingOtp}
                                    className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {resendTimer > 0 ? (
                                      `Resend in ${resendTimer}s`
                                    ) : (
                                      'Resend OTP'
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Error Display */}
                          {otpError && (
                            <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              <AlertCircle className="h-4 w-4" />
                              <span>{otpError}</span>
                            </div>
                          )}
                        </div>
                        {errors.mobile && !isPhoneVerified && (
                          <p className="text-sm text-red-600 font-medium">{errors.mobile}</p>
                        )}
                      </div>

                      {/* Gender */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Gender / लिंग *
                        </label>
                        <select
                          name="gender"
                          id="field-gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border border-zinc-400 rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 text-sm sm:text-base"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male / पुरुष</option>
                          <option value="female">Female / महिला</option>
                          <option value="other">Other / अन्य</option>
                        </select>
                        {errors.gender && <p className="text-sm text-red-600 font-medium">{errors.gender}</p>}
                      </div>

                      {/* Teacher Category */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Teacher Category / शिक्षक श्रेणी *
                        </label>
                        <select
                          name="teacherCategory"
                          id="field-teacherCategory"
                          value={formData.teacherCategory}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border border-zinc-400 rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 text-sm sm:text-base"
                        >
                          <option value="">Select Category</option>
                          <option value="bpsc">BPSC / बीपीएससी</option>
                          <option value="exclusive">Exclusive / विशिष्ट</option>
                          <option value="headteacher/headmaster">Head Teacher/Headmaster / प्रधानाध्यापक</option>
                        </select>
                        {errors.teacherCategory && <p className="text-sm text-red-600 font-medium">{errors.teacherCategory}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-200 to-pink-300 rounded-full flex items-center justify-center shadow-lg">
                        <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-purple-700" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl lg:text-lg font-bold text-zinc-900">Professional Information</h3>
                        <p className="text-zinc-700 text-xs sm:text-sm">व्यावसायिक जानकारी</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                      {/* Class */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Class / कक्षा *
                        </label>
                        <select
                          name="class"
                          id="field-class"
                          value={formData.class}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border border-zinc-400 rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 text-sm sm:text-base"
                        >
                          <option value="">Select Class</option>
                          <option value="1-5">1-5 (Primary / प्राथमिक)</option>
                          <option value="6-8">6-8 (Middle / मध्य)</option>
                          <option value="9-10">9-10 (High School / हाई स्कूल)</option>
                          <option value="11-12">11-12 (Higher Secondary / उच्च माध्यमिक)</option>
                        </select>
                        {errors.class && <p className="text-sm text-red-600 font-medium">{errors.class}</p>}
                      </div>

                      {/* Subject */}
                      <div className="space-y-1.5">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Subject / विषय *
                        </label>
                        <select
                          name="subject"
                          id="field-subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          disabled={formData.teacherCategory === 'headteacher/headmaster'}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm border border-zinc-400 rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 text-sm sm:text-base disabled:bg-zinc-100 disabled:text-zinc-500"
                        >
                          {formData.teacherCategory === 'headteacher/headmaster' ? (
                            <option value="school-head">School-based (no subject)</option>
                          ) : (
                            <>
                              <option value="">Select Subject</option>
                              {(subjectOptionsByClass[formData.class] || []).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </>
                          )}
                        </select>
                        {errors.subject && <p className="text-sm text-red-600 font-medium">{errors.subject}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Current School Information */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-200 to-emerald-300 rounded-full flex items-center justify-center shadow-lg">
                        <School className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl lg:text-lg font-bold text-zinc-900">Current School Information</h3>
                        <p className="text-zinc-700 text-xs sm:text-sm">वर्तमान स्कूल की जानकारी</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4">
                      {/* Current School UDISE */}
                      <div className="space-y-1.5 lg:relative">
                        <label className="block text-sm font-semibold text-zinc-800">
                          Current School UDISE Code or Name / वर्तमान स्कूल UDISE कोड या नाम *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="currentSchoolUdise"
                            id="field-currentSchoolUdise"
                            value={formData.currentSchoolUdise}
                            onChange={handleInputChange}
                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 lg:py-2 lg:text-sm pr-12 border rounded-xl focus:ring-2 focus:ring-zinc-600 focus:border-zinc-600 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md text-zinc-900 placeholder-zinc-700 text-sm sm:text-base ${errors.currentSchoolUdise ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-zinc-400'}`}
                            placeholder="Enter UDISE code or search by school name"
                            disabled={!!formData.currentSchoolName}
                          />
                          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-500">
                            <Search className="h-5 w-5" />
                          </div>
                        </div>
                        {errors.currentSchoolUdise && <p className="text-sm text-red-600 font-medium">{errors.currentSchoolUdise}</p>}
                        {errors.currentSchoolName && <p className="text-sm text-red-600 font-medium">{errors.currentSchoolName}</p>}
                        
                        {/* Enhanced School Search Results */}
                        {showSchoolSearch && (
                          <div className="mt-4 lg:mt-1 lg:absolute lg:left-0 lg:right-0 lg:top-full lg:z-30 border border-zinc-300 rounded-xl p-2 sm:p-4 bg-white/95 backdrop-blur-sm shadow-xl w-full max-w-full overflow-x-hidden">
                            <div className="space-y-3 max-h-60 overflow-y-auto w-full max-w-full">
                              {isSearching && schoolSearchQuery.length >= 2 && (
                                <div className="text-center py-4">
                                  <Loader2 className="h-6 w-6 animate-spin text-zinc-400 mx-auto mb-2" />
                                  <p className="text-sm text-zinc-500">Searching schools...</p>
                                </div>
                              )}
                              
                              {!isSearching && searchError && (
                                <div className="text-center py-8">
                                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
                                  <p className="text-sm text-red-600">{searchError}</p>
                                </div>
                              )}
                              
                              {!isSearching && !searchError && schoolSearchResults.map((school) => (
                                <div
                                  key={school.id}
                                  onClick={() => selectSchool(school)}
                                  className="p-4 bg-white rounded-lg border border-zinc-300 cursor-pointer hover:bg-zinc-50 transition-all duration-200 hover:shadow-md hover:border-zinc-400"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-zinc-900 mb-1">{school.name}</h4>
                                      <div className="flex items-center space-x-4 text-sm text-zinc-600">
                                        <span className="flex items-center">
                                          <MapPin className="h-3 w-3 mr-1" />
                                          {school.district_name}, {school.block_name}
                                        </span>
                                        <span className="font-mono text-xs bg-zinc-100 px-2 py-1 rounded">
                                          UDISE: {school.udise}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                        school.type === 'primary' 
                                          ? 'bg-blue-100 text-blue-700' 
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        {school.type === 'primary' ? 'Primary' : 'Secondary+'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              
                              {!isSearching && !searchError && schoolSearchResults.length === 0 && schoolSearchQuery.length >= 2 && (
                                <div className="text-center py-8">
                                  <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm text-zinc-500">No schools found</p>
                                  <p className="text-xs text-zinc-400 mt-1">Try different keywords or UDISE code</p>
                                </div>
                              )}
                              
                              {schoolSearchQuery.length < 2 && (
                                <div className="text-center py-8">
                                  <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm text-zinc-500">Type at least 2 characters to search</p>
                                  <p className="text-xs text-zinc-400 mt-1">Search by school name or UDISE code</p>
                                </div>
                              )}
                            </div>
                            
                            {schoolSearchResults.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-zinc-300">
                                <p className="text-xs text-zinc-600 text-center">
                                  Found {schoolSearchResults.length} schools. Click to select.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Selected School Display */}
                      {formData.currentSchoolName && (
                        <div className="p-6 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 rounded-xl shadow-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-green-800">
                              <CheckCircle2 className="h-6 w-6 mr-3" />
                              <div>
                                <div className="font-semibold text-lg">Selected School: {formData.currentSchoolName}</div>
                                <div className="text-sm">UDISE: {formData.currentSchoolUdise}</div>
                                {formData.blockName && (
                                  <div className="text-sm">Block: {formData.blockName}</div>
                                )}
                                {formData.districtName && (
                                  <div className="text-sm">District: {formData.districtName}</div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={clearSchoolSelection}
                              className="px-3 py-1 text-sm text-green-800 hover:text-green-900 bg-green-200 hover:bg-green-300 rounded-lg transition-colors"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preferred Transfer Choices Section */}
                  <div id="preferences-section">
                    <PreferenceCards 
                      preferences={preferences}
                      setPreferences={setPreferences}
                      schoolType={schoolType}
                    />
                    {errors.preferences && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl mt-4">
                        <div className="flex items-center gap-3">
                          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-red-800 text-sm">Transfer Preferences Error</div>
                            <div className="text-red-700 text-xs mt-1">{errors.preferences}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Account Verification Error Display */}
                  {errors.account && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-red-800 text-sm">Account Verification Required</div>
                          <div className="text-red-700 text-xs mt-1">{errors.account}</div>
                          {!user?.is_verified && user && (
                            <div className="mt-3 text-xs text-red-600">
                              ⚠️ To verify your account, scroll up to the WhatsApp number field and complete OTP verification.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-end pt-6 sm:pt-8 border-t border-zinc-300">
                    <button
                      type="submit"
                      disabled={isSubmitting || !user}
                      className={`inline-flex items-center justify-center px-6 sm:px-10 py-3 sm:py-4 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform text-sm sm:text-base ${
                        !user
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
                          : isSubmitting
                          ? 'bg-gradient-to-r from-zinc-800 via-zinc-900 to-slate-900 text-white opacity-50 cursor-not-allowed hover:scale-100'
                          : 'bg-gradient-to-r from-zinc-800 via-zinc-900 to-slate-900 text-white hover:scale-105'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2 sm:mr-3"></div>
                          Processing Application...
                        </>
                      ) : !user ? (
                        <>
                          <LogIn className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                          Login Required
                        </>
                      ) : (
                        <>
                          <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                          Submit Application
                        </>
                      )}
                    </button>
                <Link 
                  href="/bihar/mutual-transfer"
                      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-zinc-700 font-bold rounded-xl border-2 border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50 transition-all duration-300 hover:scale-105 transform shadow-md text-sm sm:text-base"
                >
                      <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                  Back to Portal
                </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* OTP Verification Modal */}
        <OtpVerificationModal
          isOpen={showOtpVerificationModal}
          onClose={() => setShowOtpVerificationModal(false)}
          onVerificationSuccess={handleOtpVerificationSuccess}
          initialPhoneNumber={formData.mobile}
          onPhoneNumberChange={handlePhoneNumberChangeFromModal}
          userName={formData.name}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirmSubmit}
          formData={formData}
          preferences={preferences}
          schoolType={schoolType}
          isSubmitting={isSubmitting}
        />
        {guideHelpButton}
        <GuideModal isOpen={isGuideModalOpen} onClose={() => setIsGuideModalOpen(false)} onStartForm={() => setIsAuthModalOpen(true)} />
      </div>
    </>
  );
} 